import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PlaybackStep } from "../selfInjectionGuide";
import "./GuideStepMedia.css";

const isVideo = (url: string | null): url is string =>
  Boolean(url && /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url));

const audioTimeLabel = (seconds: number) => {
  const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, "0")}`;
};

/** Mounted only while the guide is open. Video and narration have independent
 * controls/clocks: looping or seeking the demonstration never restarts audio. */
export function GuideStepMedia({ step }: { step: PlaybackStep }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [needsPlay, setNeedsPlay] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioStatus = audioError
    ? "The voiceover couldn’t load. Open the transcript below or try replaying it."
    : needsPlay
      ? "Press play to hear this step."
      : "";

  useEffect(() => {
    let current = true;
    const video = videoRef.current;
    const audio = audioRef.current;
    setNeedsPlay(false);
    setAudioError(false);
    setVideoError(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (video) {
      video.load();
      if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        void video.play().catch(() => {
          /* Native controls remain available. */
        });
      }
    }
    if (audio) {
      audio.load();
      void audio.play().catch(() => {
        // Sound autoplay can be blocked on initial entry, especially on iOS.
        // Keep a manual play button available without claiming audio is playing.
        if (current) setNeedsPlay(true);
      });
    }
    return () => {
      current = false;
      video?.pause();
      audio?.pause();
    };
  }, [step.media_url, step.audio_url]);

  const playAudio = (restart = false) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.error) audio.load();
    if (restart || audio.ended) audio.currentTime = 0;
    const source = audio.src;
    void audio.play().catch(() => {
      if (audioRef.current === audio && audio.src === source)
        setNeedsPlay(true);
    });
  };

  return (
    <>
      <div className="bg-inverse aspect-video w-full">
        {isVideo(step.media_url) ? (
          <video
            ref={videoRef}
            src={step.media_url}
            aria-label={`Step ${step.index}: ${step.title} demonstration`}
            className="w-full h-full object-contain"
            controls
            playsInline
            preload="metadata"
            muted
            loop
            onError={() => setVideoError(true)}
          />
        ) : step.media_url ? (
          <img
            src={step.media_url}
            alt={step.title}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="h-full flex items-center justify-center type-label-xs text-white">
            Follow the instructions below
          </div>
        )}
      </div>
      {videoError && (
        <p
          role="status"
          className="px-15 md:px-25 pt-15 type-body-sm text-ink-secondary"
        >
          The video couldn’t load. You can still follow the written instructions
          below.
        </p>
      )}
      {step.audio_url && (
        <section
          aria-label="Step voiceover"
          className="border-b border-line-subtle px-15 md:px-25 py-15 flex flex-col gap-10"
        >
          <div className="flex items-center justify-between gap-10">
            <span className="type-label-sm text-ink-secondary">Voiceover</span>
            <button
              type="button"
              onClick={() => playAudio(true)}
              className="min-h-45 inline-flex items-center gap-5 px-10 type-body-sm hover:bg-sunken"
            >
              <RotateCcw size={14} aria-hidden /> Replay voiceover
            </button>
          </div>
          <audio
            ref={audioRef}
            src={step.audio_url}
            preload="metadata"
            // The player below controls this element without browser-specific chrome.
            className="hidden"
            onPlay={() => {
              setNeedsPlay(false);
              setAudioError(false);
              setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onLoadedMetadata={(event) =>
              setDuration(
                Number.isFinite(event.currentTarget.duration)
                  ? event.currentTarget.duration
                  : 0,
              )
            }
            onTimeUpdate={(event) =>
              setCurrentTime(event.currentTarget.currentTime)
            }
            onVolumeChange={(event) => setMuted(event.currentTarget.muted)}
            onError={() => {
              setAudioError(true);
              setPlaying(false);
            }}
          />
          <div className="guide-voiceover flex items-center gap-10 bg-sunken pr-5">
            <button
              type="button"
              aria-label={playing ? "Pause voiceover" : "Play voiceover"}
              className="h-45 w-45 shrink-0 inline-flex items-center justify-center hover:bg-raised"
              onClick={() =>
                playing ? audioRef.current?.pause() : playAudio()
              }
            >
              {playing ? (
                <Pause size={16} aria-hidden />
              ) : (
                <Play size={16} aria-hidden />
              )}
            </button>
            <span
              className="font-mono text-11 whitespace-nowrap tabular-nums"
              aria-hidden="true"
            >
              {audioTimeLabel(currentTime)} / {audioTimeLabel(duration)}
            </span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(currentTime, duration)}
              disabled={!duration || audioError}
              aria-label="Seek voiceover"
              aria-valuetext={`${audioTimeLabel(currentTime)} of ${audioTimeLabel(duration)}`}
              className="guide-voiceover-seek min-w-0 flex-1"
              onChange={(event) => {
                const time = Number(event.currentTarget.value);
                if (audioRef.current) audioRef.current.currentTime = time;
                setCurrentTime(time);
              }}
            />
            <button
              type="button"
              aria-label={muted ? "Unmute voiceover" : "Mute voiceover"}
              className="h-45 w-45 shrink-0 inline-flex items-center justify-center hover:bg-raised"
              onClick={() => {
                if (audioRef.current)
                  audioRef.current.muted = !audioRef.current.muted;
              }}
            >
              {muted ? (
                <VolumeX size={16} aria-hidden />
              ) : (
                <Volume2 size={16} aria-hidden />
              )}
            </button>
          </div>
          {/* Keep the live region mounted for announcements, without reserving
              any visible space when there is nothing to say. */}
          <p
            role="status"
            className={cn(
              "type-body-sm text-ink-secondary",
              !audioStatus && "sr-only",
            )}
          >
            {audioStatus}
          </p>
        </section>
      )}
    </>
  );
}
